import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { push } from 'connected-react-router';

import {
  fetchTeam,
  fetchUser,
  fetchUsers,
} from './actions';
import { GetUsersResponse } from './api';
import { selectors } from './reducers';
import {
  Environment,
  Status,
  OwnerItem,
  Environments,
  toRelativeDateRange,
  AbsoluteDateRange,
} from './types';

const urlFields = {
  environment: 'environment',
  search: 'search',
  status: 'status',
  owner: {
    id: 'ownerId',
    type: 'ownerType',
  },
  browser: {
    [Environments.VDC]: 'browserVdc',
  },
  device: {
    [Environments.RDC]: 'deviceRdc',
    [Environments.VDC]: 'deviceVdc',
  },
  platform: {
    [Environments.RDC]: 'platformRdc',
    [Environments.VDC]: 'platformVdc',
  },
  build: {
    [Environments.RDC]: 'buildRdc',
    [Environments.VDC]: 'buildVdc',
  },
  tag: {
    [Environments.RDC]: 'tagRdc',
    [Environments.VDC]: 'tagVdc',
  },
};

export function saveTags(tags: string[], env: Environment)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.tag[env], tags.join(','));
    location.search = params.toString();
    dispatch(push(location));
  };
}

export function saveEnvironment(env: Environment)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  window.localStorage.setItem('filterBar/environment', env);
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.environment, env);
    location.search = params.toString();

    dispatch(push(location));
  };
}

export function saveStatuses(statuses: Status[])
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.status, statuses.join(','));
    location.search = params.toString();

    dispatch(push(location));
  };
}

export function saveSearch(search: string)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.search, search);
    location.search = params.toString();

    dispatch(push(location));
  };
}

export function saveOwner(owner: OwnerItem | null)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    if (owner) {
      const location = selectors.selectLocation(getState());
      const params = new URLSearchParams(location.search);
      params.set(urlFields.owner.id, owner.id);
      params.set(urlFields.owner.type, owner.type);
      location.search = params.toString();
      dispatch(push(location));
    }
  };
}

export function resetOwner() : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());

    const params = new URLSearchParams(location.search);
    params.delete(urlFields.owner.id);
    params.delete(urlFields.owner.type);

    location.search = params.toString();
    dispatch(push(location));
  };
}

export function saveBuilds(builds: string[], environment: Environment)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.build[environment], builds.join(','));
    location.search = params.toString();
    dispatch(push(location));
  };
}

export function savePlatforms(platforms: string[], environment: Environment)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.platform[environment], platforms.join(','));
    location.search = params.toString();
    dispatch(push(location));
  };
}

export function saveBrowsers(browsers: string[], environment: Environment)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.browser[environment], browsers.join(','));
    location.search = params.toString();
    dispatch(push(location));
  };
}

export function saveDevices(devices: string[], environment: Environment)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);
    params.set(urlFields.device[environment], devices.join(','));
    location.search = params.toString();
    dispatch(push(location));
  };
}

export function resetFilters(): ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const oldParams = new URLSearchParams(location.search);
    const newParams = new URLSearchParams();

    // Only Environment & Search need to be kept.
    newParams.set(urlFields.environment, oldParams.get(urlFields.environment) ?? Environments.VDC);
    newParams.set(urlFields.search, oldParams.get(urlFields.search) ?? '');

    location.search = newParams.toString();
    dispatch(push(location));
  };
}

export function saveOwnerByName(name: string)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return async (dispatch, getState) => {
    const users = selectors.selectAllUsers(getState());
    const location = selectors.selectLocation(getState());

    const owner = users.find((user) => user.username === name);
    if (owner) {
      const params = new URLSearchParams(location.search);
      params.set('ownerId', owner.id);
      params.set('ownerType', 'user');
      location.search = params.toString();
      dispatch(push(location));
    } else if (!owner) {
      const action = await dispatch(fetchUsers({ phrase: name }));
      if (!action.payload) {
        return;
      }
      const resp = action.payload as GetUsersResponse;

      if (resp.count > 0) {
        const user = resp.results[0];
        const params = new URLSearchParams(location.search);
        params.set('ownerId', user.id);
        params.set('ownerType', 'user');
        location.search = params.toString();
        dispatch(push(location));
      }
    }
  };
}

export function saveDateRange(range: AbsoluteDateRange)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return (dispatch, getState) => {
    const location = selectors.selectLocation(getState());
    const params = new URLSearchParams(location.search);

    const rel = toRelativeDateRange(range);
    if (rel) {
      params.set('start', rel);
      params.delete('end');
    } else {
      params.set('start', range.start.toString());
      params.set('end', range.end.toString());
    }
    location.search = params.toString();

    dispatch(push(location));
  };
}

/**
 * Lookup a given user or team. If the lookup fails, reset the owner filter.
 */
export function validateOwner(owner: OwnerItem)
  : ThunkAction<void, unknown, unknown, AnyAction> {
  return async (dispatch) => {
    const { id, type } = owner;
    let resolved = false;

    if (type === 'user') {
      const action = await dispatch(fetchUser({ id }));
      resolved = action.meta.requestStatus === 'fulfilled';
    } else if (type === 'team') {
      const action = await dispatch(fetchTeam({ id }));
      resolved = action.meta.requestStatus === 'fulfilled';
    }

    if (!resolved) {
      dispatch(resetOwner());
    }
  };
}
